import type { GuideLine } from "@cs2/contracts";

export const GUIDE_EDGE_THRESHOLD = 0.08;
export const GUIDE_MIN_DRAG = 0.02;

export type GuideOrientation = "horizontal" | "vertical";

export interface GuideDragStart {
  orientation: GuideOrientation;
  startPosition: number;
}

export type GuideLineDraft = Omit<GuideLine, "id">;

/** Ermittelt, ob eine Hilfslinie an einem Bildrand gestartet wurde. */
export function getGuideDragStart(
  point: { x: number; y: number },
  edgeThreshold = GUIDE_EDGE_THRESHOLD,
): GuideDragStart | null {
  const distances = [
    { edge: "top", distance: point.y },
    { edge: "bottom", distance: 1 - point.y },
    { edge: "left", distance: point.x },
    { edge: "right", distance: 1 - point.x },
  ] as const;
  const nearest = distances.reduce((current, candidate) =>
    candidate.distance < current.distance ? candidate : current,
  );

  if (nearest.distance > edgeThreshold) return null;
  if (nearest.edge === "top" || nearest.edge === "bottom") {
    return { orientation: "horizontal", startPosition: point.y };
  }
  return { orientation: "vertical", startPosition: point.x };
}

/** Spannt die gezogene Hilfslinie über die komplette Bildkante. */
export function guideLineFromDrag(
  drag: GuideDragStart,
  point: { x: number; y: number },
): GuideLineDraft {
  if (drag.orientation === "horizontal") {
    const y = Math.min(1, Math.max(0, point.y));
    return { x1: 0, y1: y, x2: 1, y2: y };
  }
  const x = Math.min(1, Math.max(0, point.x));
  return { x1: x, y1: 0, x2: x, y2: 1 };
}

export function guideDragMoved(
  drag: GuideDragStart,
  point: { x: number; y: number },
  minimum = GUIDE_MIN_DRAG,
) {
  const position = drag.orientation === "horizontal" ? point.y : point.x;
  return Math.abs(position - drag.startPosition) >= minimum;
}

/** Nativer 16:9-Koordinatenraum der Hilfslinien-Overlays; deckungsgleich mit dem Crop-Ergebnis. */
const VIEW_WIDTH = 1600;
const VIEW_HEIGHT = 900;

/**
 * Zeichnet Hilfslinien über ein `background-size:cover`-Bild. `preserveAspectRatio="xMidYMid
 * slice"` repliziert exakt das Cover-Verhalten des Hintergrundbilds, damit die Linien
 * unabhängig vom tatsächlichen Container-Seitenverhältnis über der richtigen Bildstelle
 * liegen. `vector-effect="non-scaling-stroke"` hält die Strichstärke in Bildschirmpixeln
 * konstant — in der kleinen Quickview-Kachel bleibt sie sichtbar kräftig, im großen Dialog
 * wirkt sie fein/präzise statt aufdringlich.
 */
export function GuideLinesOverlay({
  guides,
  className,
}: {
  guides: GuideLine[];
  className?: string;
}) {
  if (guides.length === 0) return null;
  return (
    <svg
      className={className}
      viewBox={`0 0 ${VIEW_WIDTH} ${VIEW_HEIGHT}`}
      preserveAspectRatio="xMidYMid slice"
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        pointerEvents: "none",
      }}
      aria-hidden="true"
    >
      {guides.map((guide) => (
        <g key={guide.id}>
          <line
            x1={guide.x1 * VIEW_WIDTH}
            y1={guide.y1 * VIEW_HEIGHT}
            x2={guide.x2 * VIEW_WIDTH}
            y2={guide.y2 * VIEW_HEIGHT}
            stroke="#04080a"
            strokeOpacity={0.85}
            strokeWidth={4}
            strokeLinecap="round"
            vectorEffect="non-scaling-stroke"
          />
          <line
            x1={guide.x1 * VIEW_WIDTH}
            y1={guide.y1 * VIEW_HEIGHT}
            x2={guide.x2 * VIEW_WIDTH}
            y2={guide.y2 * VIEW_HEIGHT}
            stroke="var(--amber)"
            strokeWidth={1.75}
            strokeLinecap="round"
            vectorEffect="non-scaling-stroke"
          />
        </g>
      ))}
    </svg>
  );
}
